import { TrendingUp, TrendingDown } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';

const s = {
  row: {
    display: 'flex',
    gap: 12,
    flexWrap: 'nowrap',
    overflowX: 'auto',
    paddingBottom: 4,
  },
  card: {
    borderRadius: 12,
    padding: '14px 16px',
    minWidth: 170,
    flex: 1,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    transition: 'transform 0.18s, box-shadow 0.18s',
    cursor: 'default',
    position: 'relative',
    overflow: 'hidden',
  },
  cardLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    marginBottom: 6,
    opacity: 0.85,
  },
  mainValue: {
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: '-0.03em',
    lineHeight: 1.1,
    marginBottom: 8,
  },
  pctBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    padding: '2px 7px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 700,
  },
  divider: {
    height: 1,
    background: 'rgba(255,255,255,0.2)',
    margin: '8px 0',
  },
  subRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 11.5,
    marginTop: 4,
  },
  subLabel: { opacity: 0.8 },
  subValue: { fontWeight: 700 },
  gloss: {
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: '50%',
    background: 'linear-gradient(180deg,rgba(255,255,255,0.12),transparent)',
    borderRadius: '12px 12px 0 0',
    pointerEvents: 'none',
  },
  // Skeleton
  skRow: { display: 'flex', gap: 12, flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: 4 },
  skCard: {
    borderRadius: 12,
    padding: '14px 16px',
    minWidth: 170,
    flex: 1,
    background: '#e2e8f0',
  },
  skLine: { borderRadius: 6, background: 'rgba(0,0,0,0.08)', marginBottom: 8 },
};

const COLORS = {
  red:      { background: 'linear-gradient(135deg,#c53030,#9b2c2c)', color: '#fff' },
  orange:   { background: 'linear-gradient(135deg,#c05621,#9c4221)', color: '#fff' },
  amber:    { background: 'linear-gradient(135deg,#d69e2e,#b7791f)', color: '#fff' },
  yellow:   { background: 'linear-gradient(135deg,#b7a000,#92800a)', color: '#fff' },
  lime:     { background: 'linear-gradient(135deg,#4a7c1f,#3a6317)', color: '#fff' },
  green:    { background: 'linear-gradient(135deg,#276749,#2f855a)', color: '#fff' },
  teal:     { background: 'linear-gradient(135deg,#285e61,#234e52)', color: '#fff' },
  cyan:     { background: 'linear-gradient(135deg,#0e7490,#155e75)', color: '#fff' },
  blue:     { background: 'linear-gradient(135deg,#2b6cb0,#2c5282)', color: '#fff' },
  indigo:   { background: 'linear-gradient(135deg,#3730a3,#312e81)', color: '#fff' },
  violet:   { background: 'linear-gradient(135deg,#6d28d9,#5b21b6)', color: '#fff' },
  purple:   { background: 'linear-gradient(135deg,#7b2d8b,#6b21a8)', color: '#fff' },
  pink:     { background: 'linear-gradient(135deg,#9d174d,#831843)', color: '#fff' },
  rose:     { background: 'linear-gradient(135deg,#be185d,#9d174d)', color: '#fff' },
  slate:    { background: 'linear-gradient(135deg,#334155,#1e293b)', color: '#fff' },
  steel:    { background: 'linear-gradient(135deg,#1e4976,#153657)', color: '#fff' },
};

function Pct({ value }) {
  const up = value >= 50;
  return (
    <span style={{
      ...s.pctBadge,
      background: 'rgba(255,255,255,0.2)',
      color: '#fff',
    }}>
      {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
      {value}%
    </span>
  );
}

function CardHover({ children, style }) {
  return (
    <div
      style={{ ...s.card, ...style }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,0.18)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)';
      }}
    >
      <div style={s.gloss} />
      {children}
    </div>
  );
}

function SkeletonCards() {
  return (
    <div style={s.skRow}>
      {[...Array(5)].map((_, i) => (
        <div key={i} style={s.skCard} className="skeleton">
          <div style={{ ...s.skLine, height: 10, width: '50%' }} />
          <div style={{ ...s.skLine, height: 28, width: '80%' }} />
          <div style={{ ...s.skLine, height: 1 }} />
          <div style={{ ...s.skLine, height: 12, width: '70%' }} />
          <div style={{ ...s.skLine, height: 12, width: '60%', marginBottom: 0 }} />
        </div>
      ))}
    </div>
  );
}

export default function KpiCards() {
  const { data, loading } = useDashboard();

  if (!data) return <SkeletonCards />;

  const { mtd, ytd, oa, trans, ec } = data.kpi;

  return (
    <div style={{ ...s.row, opacity: loading ? 0.55 : 1, transition: 'opacity 0.2s' }}>
      {/* MTD */}
      <CardHover style={COLORS.red}>
        <div style={s.cardLabel}>MTD Sales</div>
        <div style={s.mainValue}>{mtd.value}</div>
        <div style={s.divider} />
        <div style={s.subRow}><span style={s.subLabel}>Vs Last</span><Pct value={mtd.vsLast} /></div>
        <div style={s.subRow}><span style={s.subLabel}>Vs Tgt</span><Pct value={mtd.vsTgt} /></div>
      </CardHover>

      {/* YTD */}
      <CardHover style={COLORS.yellow}>
        <div style={s.cardLabel}>YTD Sales</div>
        <div style={s.mainValue}>{ytd.value}</div>
        <div style={s.divider} />
        <div style={s.subRow}><span style={s.subLabel}>Vs Last</span><Pct value={ytd.vsLast} /></div>
        <div style={s.subRow}><span style={s.subLabel}>Vs Tgt</span><Pct value={ytd.vsTgt} /></div>
      </CardHover>

      {/* OA/CB */}
      <CardHover style={COLORS.teal}>
        <div style={s.cardLabel}>Order Achievement</div>
        <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 4 }}>
          OA/CB &nbsp;
          <span style={{ fontSize: 16 }}>{oa.oaCb.actual.toLocaleString()} / {oa.oaCb.target.toLocaleString()}</span>
          &nbsp;<Pct value={oa.oaCb.pct} />
        </div>
        <div style={s.divider} />
        <div style={s.subRow}>
          <span style={s.subLabel}>OA / Tgt</span>
          <span style={s.subValue}>{oa.oaTgt.actual.toLocaleString()} &nbsp;<Pct value={oa.oaTgt.pct} /></span>
        </div>
        <div style={s.subRow}>
          <span style={s.subLabel}>CB / Tgt</span>
          <span style={s.subValue}>{oa.cbTgt.actual.toLocaleString()} &nbsp;<Pct value={oa.cbTgt.pct} /></span>
        </div>
      </CardHover>

      {/* Trans */}
      <CardHover style={COLORS.green}>
        <div style={s.cardLabel}>Transaction</div>
        <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 4 }}>
          Trans / Tgt &nbsp;
          <span style={{ fontSize: 15 }}>{trans.transVsTgt.actual} / {trans.transVsTgt.target}</span>
          &nbsp;<Pct value={trans.transVsTgt.pct} />
        </div>
        <div style={s.divider} />
        <div style={s.subRow}>
          <span style={s.subLabel}>IPT Vs Tgt</span>
          <span style={s.subValue}>{trans.iptVsTgt.actual} / {trans.iptVsTgt.target} &nbsp;<Pct value={trans.iptVsTgt.pct} /></span>
        </div>
        <div style={s.subRow}>
          <span style={s.subLabel}>Pact Vs Tgt</span>
          <span style={s.subValue}>{trans.pactVsTgt.actual} / {trans.pactVsTgt.target} &nbsp;<Pct value={trans.pactVsTgt.pct} /></span>
        </div>
      </CardHover>

      {/* EC/Call */}
      <CardHover style={COLORS.indigo}>
        <div style={s.cardLabel}>Effective Call</div>
        <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 4 }}>
          EC / Call &nbsp;
          <span style={{ fontSize: 15 }}>{ec.ecCall.actual.toLocaleString()} / {ec.ecCall.target.toLocaleString()}</span>
          &nbsp;<Pct value={ec.ecCall.pct} />
        </div>
        <div style={s.divider} />
        <div style={s.subRow}>
          <span style={s.subLabel}>EC Vs Tgt</span>
          <span style={s.subValue}>{ec.ecVsTgt.actual.toLocaleString()} &nbsp;<Pct value={ec.ecVsTgt.pct} /></span>
        </div>
        <div style={s.subRow}>
          <span style={s.subLabel}>Call Vs Tgt</span>
          <span style={s.subValue}>{ec.callVsTgt.actual.toLocaleString()} &nbsp;<Pct value={ec.callVsTgt.pct} /></span>
        </div>
      </CardHover>
    </div>
  );
}
